import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  InputAdornment,
  Container,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./CreateRuleComponent.css";

const CreateRuleComponent = ({
  activeStep,
  handleNext,
  handleBack,
  selectedDepartment,
  handleDepartmentSelect,
  availableDepartments,
  fetchingDepartments,
  searchTerm,
  handleSearchChange,
  resetEnabled,
  carryForwardEnabled,
  handleTypeChange,
  resetConfig,
  handleResetConfigChange,
  handleResetFrequencyChange,
  handleResetDayChange,
  handleResetDateSelect,
  carryForwardConfig,
  handleCarryForwardConfigChange,
  handleCarryForwardFrequencyChange,
  handleCarryForwardDayChange,
  handleCarryForwardDateSelect,
  loading,
}) => {
  const steps = [""];

  // Days of week
  const weekDays = [
    { value: 1, label: "Mon" },
    { value: 2, label: "Tue" },
    { value: 3, label: "Wed" },
    { value: 4, label: "Thu" },
    { value: 5, label: "Fri" },
    { value: 6, label: "Sat" },
    { value: 7, label: "Sun" },
  ];

  // Frequency options
  const frequencyOptions = [
    { value: "DAILY", label: "Daily" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "MONTHLY", label: "Monthly" },
  ];

  // Generate calendar days for monthly selection
  const generateCalendarDays = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(i);
    }
    return days;
  };

  // Calendar days
  const calendarDays = generateCalendarDays();

  // Handle number input with validation
  const handleNumberInput = (handler) => (e) => {
    const value = e.target.value;
    // Allow only numbers
    if (value === "" || /^\d+$/.test(value)) {
      handler(value);
    }
  };

  // Prevent wheel events
  const handleWheel = (e) => {
    e.preventDefault();
    e.target.blur();
  };

  // Prevent arrow keys
  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderDepartmentSelection();
      case 1:
        return renderRechargeTypeSelection();
      case 2:
        return renderConfiguration();
      default:
        return null;
    }
  };

  // Department Selection Step
  const renderDepartmentSelection = () => (
    <Box className="step-content">
      <Paper elevation={0} className="step-paper">
        <Box className="step-header">
          <Typography variant="h6" className="step-title">
            Select Department
          </Typography>
          <Typography variant="body2" className="step-subtitle">
            Please select a department to create a rule for
          </Typography>
        </Box>
        
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search department..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="search-icon" />
              </InputAdornment>
            ),
            className: "search-input"
          }}
          sx={{ mb: 3 }}
        />

        {fetchingDepartments ? (
          <Box className="create-loading-container">
            <CircularProgress size={40} className="create-loading-spinner" />
            <Typography variant="body2" className="create-loading-text">
              Loading departments...
            </Typography>
          </Box>
        ) : (
          <Box className="department-list">
            {availableDepartments.length === 0 ? (
              <Box className="empty-state">
                <SearchIcon className="empty-icon" />
                <Typography variant="body2" className="empty-text">
                  {searchTerm ? "No departments found matching your search" : "No departments available"}
                </Typography>
                {searchTerm && (
                  <Typography variant="caption" className="empty-subtext">
                    Try a different search term
                  </Typography>
                )}
                {!searchTerm && availableDepartments.length === 0 && (
                  <Typography variant="caption" className="empty-subtext">
                    Please contact administrator to add departments
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                {/* <Typography variant="body2" className="department-count">
                  {availableDepartments.length} department{availableDepartments.length !== 1 ? 's' : ''} available
                </Typography> */}
                <Grid container spacing={2}>
                  {availableDepartments.map((dept) => (
                    <Grid item xs={12} md={6} key={dept.id}>
                      <Card
                        className={`department-card ${selectedDepartment?.id === dept.id ? 'selected' : ''}`}
                        onClick={() => handleDepartmentSelect(dept)}
                      >
                        <CardContent className="department-card-content">
                          <Box className="department-card-header">
                            <Box>
                              <Typography variant="subtitle1" className="department-name">
                                {dept.name}
                              </Typography>
                              <Typography variant="body2" className="department-id">
                                ID: {dept.id}
                              </Typography>
                            </Box>
                            {selectedDepartment?.id === dept.id && (
                              <CheckCircleIcon className="selected-icon" />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );

  // Recharge Type Selection Step
  const renderRechargeTypeSelection = () => (
    <Box className="step-content">
      <Paper elevation={0} className="step-paper">
        <Box className="step-header">
          <Typography variant="h6" className="step-title">
            Configure Reset and Carry Forward Rules
          </Typography>
          <Typography variant="body2" className="step-subtitle">
            Configure how your employees' balances are handled at the end of the period. 
            You can select one or multiple options to proceed with the setup.
          </Typography>
        </Box>

        <Box className="type-prompt">
          <Typography variant="subtitle1" className="prompt-title">
            Which type of balance handling would you like to configure?
          </Typography>
          <Typography variant="caption" className="prompt-subtitle">
            Select at least one option to continue
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Reset Type Card */}
          <Grid item xs={12} md={6}>
            <Card
              className={`type-card ${resetEnabled ? 'selected' : ''}`}
              onClick={() => handleTypeChange('reset', !resetEnabled)}
            >
              <CardContent className="type-card-content">
                <Box className="type-card-header">
                  <Checkbox
                    checked={resetEnabled}
                    onChange={(e) => handleTypeChange('reset', e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                    className="type-checkbox"
                  />
                  <Box className="type-title-container">
                    <Typography variant="subtitle1" className="type-title">
                      Reset Type
                    </Typography>
                    <Typography variant="caption" className="type-subtitle">
                      Balance Reset
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" className="type-description">
                  Automatically reset user balances to a predefined amount at the start of each period.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Carry Forward Type Card */}
          <Grid item xs={12} md={6}>
            <Card
              className={`type-card ${carryForwardEnabled ? 'selected' : ''}`}
              onClick={() => handleTypeChange('carryForward', !carryForwardEnabled)}
            >
              <CardContent className="type-card-content">
                <Box className="type-card-header">
                  <Checkbox
                    checked={carryForwardEnabled}
                    onChange={(e) => handleTypeChange('carryForward', e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                    className="type-checkbox"
                  />
                  <Box className="type-title-container">
                    <Typography variant="subtitle1" className="type-title">
                      Carry Forward Type
                    </Typography>
                    <Typography variant="caption" className="type-subtitle">
                      Balance Rollover
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" className="type-description">
                  Allow users to roll over a portion or all of their unused balance to the next period.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  // Configuration Step
  const renderConfiguration = () => (
    <Box className="step-content">
      <Paper elevation={0} className="step-paper">
        <Box className="step-header">
          <Typography variant="h6" className="step-title">
            Balance Handling Configuration
          </Typography>
          <Typography variant="body2" className="step-subtitle">
            Configure how your employees' balances are handled at the end of the period. 
            You can select one or multiple options to proceed with the setup.
          </Typography>
        </Box>

        <Box className="selected-types-container">
          <Typography variant="subtitle1" className="selected-types-title">
            Selected Balance Handling Types
          </Typography>
          <Box className="selected-types">
            {resetEnabled && (
              <Box className="selected-type-chip">
                <CheckCircleIcon className="chip-icon" />
                <Typography variant="caption" className="chip-text">
                  Reset Type
                </Typography>
              </Box>
            )}
            {carryForwardEnabled && (
              <Box className="selected-type-chip">
                <CheckCircleIcon className="chip-icon" />
                <Typography variant="caption" className="chip-text">
                  Carry Forward Type
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Reset Configuration */}
          {resetEnabled && (
            <Grid item xs={12} md={carryForwardEnabled ? 6 : 12}>
              <Paper elevation={0} className="config-section">
                <Box className="config-header">
                  <Typography variant="subtitle1" className="config-title">
                    RESET CONFIGURATION
                  </Typography>
                  <Typography variant="caption" className="config-subtitle">
                    Configure automatic balance reset
                  </Typography>
                </Box>
                
                <Box className="config-form">
                  {/* Recharge Amount */}
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Recharge Amount
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      inputMode="numeric"
                      value={resetConfig.amount || ""}
                      onChange={handleNumberInput((value) => handleResetConfigChange('amount', value))}
                      onWheel={handleWheel}
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        className: "create-rule-amount-input "
                      }}
                      size="large"
                      placeholder="Enter amount"
                    />
                  </Box>
                  
                  {/* Frequency Selection */}
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Frequency
                    </Typography>
                    <Box className="frequency-buttons">
                      {frequencyOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={resetConfig.frequency === option.value ? "contained" : "outlined"}
                          onClick={() => handleResetFrequencyChange(option.value)}
                          className={`frequency-button ${resetConfig.frequency === option.value ? 'selected' : ''}`}
                          size="small"
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {/* Day/Date Selection */}
                  {resetConfig.frequency === "WEEKLY" && (
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Day of Reset
                      </Typography>
                      <Box className="day-buttons">
                        {weekDays.map((day) => (
                          <Button
                            key={day.value}
                            variant={resetConfig.day === day.value ? "contained" : "outlined"}
                            onClick={() => handleResetDayChange(day.value)}
                            className={`day-button ${resetConfig.day === day.value ? 'selected' : ''}`}
                            size="small"
                          >
                            {day.label}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {resetConfig.frequency === "MONTHLY" && (
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Date of Reset
                      </Typography>
                      <Box className="calendar-container">
                        <Grid container spacing={0.5}>
                          {calendarDays.map((day) => (
                            <Grid item xs={2.4} key={day}>
                              <Button
                                variant={resetConfig.day === day ? "contained" : "outlined"}
                                onClick={() => handleResetDateSelect(day)}
                                className={`calendar-day ${resetConfig.day === day ? 'selected' : ''}`}
                                size="small"
                              >
                                {day}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                        {resetConfig.day && (
                          <Typography variant="caption" className="selected-date">
                            Selected: Day {resetConfig.day}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Carry Forward Configuration */}
          {carryForwardEnabled && (
            <Grid item xs={12} md={resetEnabled ? 6 : 12}>
              <Paper elevation={0} className="config-section">
                <Box className="config-header">
                  <Typography variant="subtitle1" className="config-title">
                    CARRY FORWARD CONFIGURATION
                  </Typography>
                  <Typography variant="caption" className="config-subtitle">
                    Configure balance rollover settings
                  </Typography>
                </Box>
                
                <Box className="config-form">
                  {/* Recharge Amount */}
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Recharge Amount
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      inputMode="numeric"
                      value={carryForwardConfig.amount || ""}
                      onChange={handleNumberInput((value) => handleCarryForwardConfigChange('amount', value))}
                      onWheel={handleWheel}
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        className: "create-rule-amount-input"
                      }}
                      size="small"
                      placeholder="Enter amount"
                    />
                  </Box>

                  {/* Low Balance Limit */}
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Low Balance Limit
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      inputMode="numeric"
                      value={carryForwardConfig.lowBalance || ""}
                      onChange={handleNumberInput((value) => handleCarryForwardConfigChange('lowBalance', value))}
                      onWheel={handleWheel}
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        className: "create-rule-amount-input"
                      }}
                      helperText="Minimum balance required for carry forward"
                      size="small"
                      placeholder="Enter limit"
                    />
                  </Box>
                  
                  {/* Frequency Selection */}
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Frequency
                    </Typography>
                    <Box className="frequency-buttons">
                      {frequencyOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={carryForwardConfig.frequency === option.value ? "contained" : "outlined"}
                          onClick={() => handleCarryForwardFrequencyChange(option.value)}
                          className={`frequency-button ${carryForwardConfig.frequency === option.value ? 'selected' : ''}`}
                          size="small"
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {/* Day/Date Selection */}
                  {carryForwardConfig.frequency === "WEEKLY" && (
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Day of Reset
                      </Typography>
                      <Box className="day-buttons">
                        {weekDays.map((day) => (
                          <Button
                            key={day.value}
                            variant={carryForwardConfig.day === day.value ? "contained" : "outlined"}
                            onClick={() => handleCarryForwardDayChange(day.value)}
                            className={`day-button ${carryForwardConfig.day === day.value ? 'selected' : ''}`}
                            size="small"
                          >
                            {day.label}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {carryForwardConfig.frequency === "MONTHLY" && (
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Date of Reset
                      </Typography>
                      <Box className="calendar-container">
                        <Grid container spacing={0.5}>
                          {calendarDays.map((day) => (
                            <Grid item xs={2.4} key={day}>
                              <Button
                                variant={carryForwardConfig.day === day ? "contained" : "outlined"}
                                onClick={() => handleCarryForwardDateSelect(day)}
                                className={`calendar-day ${carryForwardConfig.day === day ? 'selected' : ''}`}
                                size="small"
                              >
                                {day}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                        {carryForwardConfig.day && (
                          <Typography variant="caption" className="selected-date">
                            Selected: Day {carryForwardConfig.day}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );

  return (
    <Container maxWidth="lg" className="main-container">
      {/* Step Content */}
      <Box className="content-container">
        {renderStepContent(activeStep)}
      </Box>

      {/* Navigation Buttons */}
      <Box className="navigation-container">
        <Button
          onClick={handleBack}
          startIcon={<KeyboardArrowLeftIcon />}
          disabled={loading}
          variant="outlined"
          className="departrule-back-button"
          size="medium"
        >
          {activeStep === 0 ? 'Previous' : 'Back'}
        </Button>

        <Button
          onClick={handleNext}
          endIcon={activeStep === steps.length - 1 ? undefined : <KeyboardArrowRightIcon />}
          variant="contained"
          disabled={loading}
          className="next-button"
          size="medium"
        >
          {loading ? (
            <CircularProgress size={20} className="button-spinner" />
          ) : activeStep === steps.length - 1 ? (
            'Create Rule'
          ) : (
            'Continue'
          )}
        </Button>
      </Box>
    </Container>
  );
};

export default CreateRuleComponent;